import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { Eye, Filter, List, MoreHorizontal, Puzzle, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  EntityContextMenu,
  type EntityContextMenuEntry,
} from '@/components/features/entity-context-menu'
import { useFlows } from '@/hooks/use-flows'
import {
  getFlowActivity,
  getFlowRunEvents,
  type FlowActivityCounts,
  type FlowActivityRun,
  type FlowRunEvent,
} from '@/lib/api'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

type OwnerScope = 'mine' | 'team'
type PulseStat = 'success24' | 'failed24' | 'exceptions7' | 'cost7'

const EMPTY_COUNTS: FlowActivityCounts = {
  successful_24h: 0,
  failed_24h: 0,
  exceptions_7d: 0,
  cost_7d: 0,
}

const STATUS_OPTIONS = ['completed', 'failed', 'running', 'pending', 'policy_blocked'] as const

function extensionName(stepIds: string[] | undefined): string | null {
  for (const id of stepIds ?? []) {
    const match = String(id).match(/basecamp|zoom|slack|gdrive|google|git/i)
    if (match) {
      const word = match[0].toLowerCase()
      return word.charAt(0).toUpperCase() + word.slice(1)
    }
  }
  return null
}

function formatDurationMs(ms: number) {
  const sec = Math.max(0, Math.round(ms / 1000))
  if (sec < 60) return `${sec}s`
  const minutes = Math.floor(sec / 60)
  const rest = sec % 60
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`
}

function formatWhen(iso: string) {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const sec = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (sec < 60) return 'Just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hours = Math.round(min / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function statusLabel(status: string) {
  if (status === 'completed') return 'Success'
  if (status === 'policy_blocked') return 'Policy blocked'
  if (status === 'failed') return 'Failed'
  if (status === 'running') return 'Running'
  if (status === 'pending') return 'Pending'
  return status
}

function statusClass(status: string) {
  if (status === 'completed') return 'border-green-500/20 bg-green-500/10 text-green-600'
  if (status === 'failed' || status === 'policy_blocked') return 'border-red-500/20 bg-red-500/10 text-red-600'
  if (status === 'running') return 'border-primary/30 bg-primary/10 text-primary'
  return 'border-border bg-muted text-muted-foreground'
}

function elapsedMs(run: FlowActivityRun, now: number) {
  const start = new Date(run.started_at).getTime()
  const end = run.completed_at ? new Date(run.completed_at).getTime() : now
  if (!Number.isFinite(start) || !Number.isFinite(end)) return run.duration_ms ?? 0
  return Math.max(0, end - start)
}

function stepRows(events: FlowRunEvent[], runStatus: string) {
  const rows = new Map<string, { id: string; state: string; output: string; conversationId: string | null }>()
  for (const event of events) {
    if (!event.step_id) continue
    if (
      event.event_type !== 'flow.step.started' &&
      event.event_type !== 'flow.step.completed'
    ) {
      continue
    }
    const row = rows.get(event.step_id) ?? {
      id: event.step_id,
      state: 'pending',
      output: '',
      conversationId: null,
    }
    if (event.event_type === 'flow.step.started') row.state = 'running'
    if (event.event_type === 'flow.step.completed') {
      row.state = 'ok'
      const result = event.payload?.result
      if (result && typeof result === 'object') {
        const body = result as Record<string, unknown>
        const text = [body.text, body.inbox_body, body.summary].find(value => typeof value === 'string')
        if (typeof text === 'string') row.output = text
        if (typeof body.conversation_id === 'string') row.conversationId = body.conversation_id
      }
    }
    rows.set(event.step_id, row)
  }
  if (runStatus === 'failed') {
    for (const row of rows.values()) {
      if (row.state === 'running') row.state = 'failed'
    }
  }
  return Array.from(rows.values())
}

export function FlowStudioPulse({
  workspaceIds,
  onOpenFlow,
}: {
  workspaceIds: string[]
  onOpenFlow: (flowId: string, tab: 'definition' | 'runs', event?: MouseEvent) => void
}) {
  const { flows } = useFlows()
  const [scope, setScope] = useState<OwnerScope>('team')
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [status, setStatus] = useState('')
  const [triggerType, setTriggerType] = useState('')
  const [flowId, setFlowId] = useState('')
  const [sinceHours, setSinceHours] = useState<number | undefined>(undefined)
  const [stat, setStat] = useState<PulseStat | null>(null)
  const [runs, setRuns] = useState<FlowActivityRun[]>([])
  const [counts, setCounts] = useState<FlowActivityCounts>(EMPTY_COUNTS)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [events, setEvents] = useState<FlowRunEvent[]>([])
  const [menu, setMenu] = useState<{ run: FlowActivityRun; x: number; y: number } | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const running = runs.some(run => run.status === 'running' || run.status === 'pending')
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query.trim()), 250)
    return () => window.clearTimeout(id)
  }, [query])

  useEffect(() => {
    if (!running) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [running])

  useEffect(() => {
    let cancelled = false
    void getFlowActivity(workspaceIds, {
      scope,
      status: status || undefined,
      triggerType: triggerType || undefined,
      flowId: flowId || undefined,
      sinceHours,
      q: debouncedQuery || undefined,
    }).then((result) => {
      if (cancelled) return
      setRuns(result.runs)
      setCounts(result.counts)
    }).catch(() => {
      if (cancelled) return
      setRuns([])
      setCounts(EMPTY_COUNTS)
    })
    return () => { cancelled = true }
  }, [workspaceIds, scope, status, triggerType, flowId, sinceHours, debouncedQuery])

  const selected = runs.find(run => run.id === selectedId) ?? null

  useEffect(() => {
    if (!selected) {
      setEvents([])
      return
    }
    let cancelled = false
    void getFlowRunEvents(selected.flow_id, selected.id).then((rows) => {
      if (!cancelled) setEvents(rows)
    }).catch(() => {
      if (!cancelled) setEvents([])
    })
    return () => { cancelled = true }
  }, [selected])

  const triggerOptions = useMemo(() => {
    const values = new Set<string>(['manual', 'schedule.cron', 'webhook'])
    for (const run of runs) {
      if (run.trigger_type) values.add(run.trigger_type)
    }
    return Array.from(values)
  }, [runs])

  const extensionOptions = useMemo(() => {
    const values = new Set<string>()
    for (const run of runs) {
      const name = extensionName(run.step_ids)
      if (name) values.add(name)
    }
    return Array.from(values)
  }, [runs])

  const [extension, setExtension] = useState('')
  const visible = extension
    ? runs.filter(run => extensionName(run.step_ids) === extension)
    : runs

  const filtersOn = Boolean(status || triggerType || flowId || sinceHours || extension || stat)

  const clearFilters = () => {
    setStatus('')
    setTriggerType('')
    setFlowId('')
    setSinceHours(undefined)
    setExtension('')
    setStat(null)
  }

  const applyStat = (id: PulseStat) => {
    setStat(id)
    setFlowId('')
    setTriggerType('')
    setExtension('')
    if (id === 'success24') {
      setStatus('completed')
      setSinceHours(24)
    } else if (id === 'failed24') {
      setStatus('failed')
      setSinceHours(24)
    } else if (id === 'exceptions7') {
      setStatus('exceptions')
      setSinceHours(168)
    } else {
      setStatus('')
      setSinceHours(168)
    }
  }

  const openTranscript = (conversationId: string, title: string) => {
    window.dispatchEvent(new CustomEvent('open-chat-conversation-main', {
      detail: { threadId: conversationId, title },
    }))
  }

  const menuEntries = (run: FlowActivityRun): EntityContextMenuEntry[] => [
    {
      label: 'View details',
      icon: Eye,
      onSelect: () => {
        setSelectedId(run.id)
        setDetailOpen(true)
      },
    },
    {
      label: 'Open flow',
      icon: List,
      onSelect: () => onOpenFlow(run.flow_id, 'definition'),
    },
    {
      label: 'Open runs',
      onSelect: () => onOpenFlow(run.flow_id, 'runs'),
    },
  ]

  const cards: { id: PulseStat; label: string; value: string }[] = [
    { id: 'success24', label: 'Successful · 24h', value: String(counts.successful_24h) },
    { id: 'failed24', label: 'Failed · 24h', value: String(counts.failed_24h) },
    { id: 'exceptions7', label: 'Exceptions · 7d', value: String(counts.exceptions_7d) },
    { id: 'cost7', label: 'Cost · 7d', value: `$${Number(counts.cost_7d || 0).toFixed(2)}` },
  ]

  const detailSteps = selected ? stepRows(events, selected.status) : []

  return (
    <div className="flex min-h-0 flex-1" data-testid={testIds.flowStudioIndex.pulse}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          <div
            className="inline-flex gap-1 rounded-[10px] bg-muted p-[3px]"
            role="tablist"
            aria-label="Owner scope"
            data-testid={testIds.flowStudioIndex.pulseScope}
          >
            {(['mine', 'team'] as const).map(option => (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={scope === option}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium capitalize',
                  scope === option ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground',
                )}
                onClick={() => setScope(option)}
              >
                {option}
              </button>
            ))}
          </div>
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search runs"
              className="h-8 pl-7 text-xs"
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant={status === 'exceptions' && !stat ? 'secondary' : 'ghost'}
            className="h-8 text-xs"
            onClick={() => {
              clearFilters()
              setStatus('exceptions')
            }}
          >
            Needs attention
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className={cn('h-8 w-8', filtersOn && 'bg-primary/10 text-primary')}
                aria-label="Filter runs"
                data-testid={testIds.flowStudioIndex.pulseFilter}
              >
                <Filter className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Flow</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {flows.map(flow => (
                    <DropdownMenuItem key={flow.id} onSelect={() => { setFlowId(flow.id); setStat(null) }}>
                      {flow.title}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Status</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {STATUS_OPTIONS.map(option => (
                    <DropdownMenuItem key={option} onSelect={() => { setStatus(option); setStat(null) }}>
                      {statusLabel(option)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Trigger</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {triggerOptions.map(option => (
                    <DropdownMenuItem key={option} onSelect={() => { setTriggerType(option); setStat(null) }}>
                      {option}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Extension</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {extensionOptions.length === 0 && (
                    <DropdownMenuItem disabled>None in this page</DropdownMenuItem>
                  )}
                  {extensionOptions.map(option => (
                    <DropdownMenuItem key={option} onSelect={() => setExtension(option)}>
                      {option}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              {filtersOn && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={clearFilters}>Clear filters</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="grid grid-cols-4 divide-x border-b border-border">
          {cards.map(card => (
            <button
              key={card.id}
              type="button"
              onClick={() => applyStat(card.id)}
              className={cn('px-3 py-2 text-left hover:bg-muted/40', stat === card.id && 'bg-primary/10')}
            >
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{card.label}</div>
              <p className="text-xl font-semibold leading-none">{card.value}</p>
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {visible.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">No runs in this view.</p>
          )}
          {visible.map(run => {
            const ext = extensionName(run.step_ids)
            const ms = elapsedMs(run, now)
            return (
              <div
                key={run.id}
                role="button"
                tabIndex={0}
                className={cn(
                  'flex w-full items-center gap-2 border-b border-border px-4 py-2 text-left hover:bg-muted/40',
                  selectedId === run.id && 'bg-primary/5',
                )}
                onClick={() => {
                  setSelectedId(run.id)
                  setDetailOpen(true)
                }}
                onDoubleClick={event => onOpenFlow(run.flow_id, 'runs', event)}
                onContextMenu={event => {
                  event.preventDefault()
                  setSelectedId(run.id)
                  setMenu({ run, x: event.clientX, y: event.clientY })
                }}
              >
                <span className="min-w-0 flex-1 truncate text-sm">{run.title}</span>
                <span className="w-16 shrink-0 text-xs text-muted-foreground">{formatWhen(run.started_at)}</span>
                <span className="flex w-6 shrink-0 justify-center" title={ext ?? undefined}>
                  {ext ? <Puzzle className="h-3.5 w-3.5 text-muted-foreground" aria-label={ext} /> : null}
                </span>
                <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-medium', statusClass(run.status))}>
                  {statusLabel(run.status)}
                </span>
                <span className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {formatDurationMs(ms)}
                </span>
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                  aria-label="More actions"
                  onClick={event => {
                    event.stopPropagation()
                    const rect = event.currentTarget.getBoundingClientRect()
                    setSelectedId(run.id)
                    setMenu({ run, x: rect.right, y: rect.bottom })
                  }}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {detailOpen && (
        <aside
          className="flex w-[min(360px,42%)] shrink-0 flex-col border-l border-border bg-background"
          data-testid={testIds.flowStudioIndex.pulseRunDetail}
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <p className="truncate text-xs font-medium">{selected?.title ?? 'Run'}</p>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDetailOpen(false)}>
              Close
            </Button>
          </div>
          {selected ? (
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
              <p className="text-xs text-muted-foreground">
                {statusLabel(selected.status)} · {formatWhen(selected.started_at)} · {formatDurationMs(elapsedMs(selected, now))}
              </p>
              {selected.error && <p className="text-xs text-red-600">{selected.error}</p>}
              {detailSteps.map(step => (
                <div key={step.id} className="rounded-md border border-border/70">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-2 py-1.5 text-left"
                    onClick={() => setExpanded(expanded === step.id ? null : step.id)}
                  >
                    <span className="min-w-0 flex-1 truncate text-xs font-medium">{step.id}</span>
                    <span className="text-[11px] text-muted-foreground">{step.state}</span>
                  </button>
                  {expanded === step.id && (
                    <div className="space-y-2 border-t border-border/60 px-2 py-2">
                      <p className="text-xs text-muted-foreground">{step.output || 'No output recorded.'}</p>
                      {(step.conversationId || selected.conversation_id) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => openTranscript(
                            step.conversationId || selected.conversation_id || '',
                            selected.title,
                          )}
                        >
                          Open transcript
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {detailSteps.length === 0 && (
                <p className="text-xs text-muted-foreground">No step events for this run.</p>
              )}
              {selected.conversation_id && detailSteps.length === 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => openTranscript(selected.conversation_id || '', selected.title)}
                >
                  Open transcript
                </Button>
              )}
            </div>
          ) : (
            <p className="p-4 text-sm text-muted-foreground">Select a run to see its steps.</p>
          )}
        </aside>
      )}

      {menu && (
        <EntityContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          data-testid={testIds.flowStudioIndex.pulseRunMenu}
          entries={menuEntries(menu.run)}
        />
      )}
    </div>
  )
}
